<template>
<link v-if="isUmbraco" rel="stylesheet" href="/assets/index.css" />

  <section :class="containerStyles" ref="scrollElement">
    <div :class="textContainerClasses">
      <div :class="['w-full flex-1', showImage ? '' : 'lg:mr-12 xl:mr-20 flex-1']">
        <paragraph
          v-if="tag"
          :is-umbraco="isUmbraco"
          :theme="themeType"
          :text="tag"
          classes="mb-5 font-bold"
        />
        <header2
          :text="title"
          :is-umbraco="isUmbraco"
          :theme="themeType"
          classes="mb-5"
        />
        <rich-text
          v-if="description"
          :text="description"
          :is-umbraco="isUmbraco"
          :theme="themeType"
        />
      </div>

      <div :class="['mt-20  flex-1', showImage ? 'lg:mt-[100-px]' : 'lg:mt-0 flex-1']">
        <accordion-cell
          v-for="item in accordionList"
          :title="item.title"
          :is-umbraco="isUmbraco"
          :themeType="themeType"
          :tag="item.tag"
          classes=""
        >
          <rich-text
            :text="item.content"
            :is-umbraco="isUmbraco"
            :theme="themeType"
            classes="mt-5 lg:mt-7"
          />
        </accordion-cell>
      </div>
    </div>
    <div v-if="image" :class="imageContainerClasses">
      <image-component :img-alt="imgAlt" :image="image" :list="false" loading="lazy" />
    </div>
    <div v-else-if="videoId" :class="imageContainerClasses">
      <vimeo-player :videoId="Number(videoId)" :portrait="true" />
    </div>
  </section>
</template>
<script setup lang="ts">
import { IAccordionProps } from "@/types/components/IAccordionProps";
import { Ref, computed, onMounted, ref, toRefs, watch } from "vue";
import { changeTheme, getThemeType } from "@/util/helper";
import { onIntersect } from "@/composables/onIntersect";
import ImageComponent from "@atoms/Image/Img.vue";
import Paragraph from "@components/Typography/Paragraph.ce.vue";
import Header2 from "@components/Typography/Header2.ce.vue";
import RichText from "@components/Typography/RichText.ce.vue";
import AccordionCell from "./AccordionCell.vue";
import VimeoPlayer from "@molecules/VimeoPlayer/VimeoPlayer.ce.vue";

const props = withDefaults(defineProps<IAccordionProps>(), {
  isUmbraco: false,
  imgPos: "right",
  componentWidth: "lg",
});
const {
  isUmbraco,
  theme,
  title,
  tag,
  image,
  imgAlt,
  description,
  items,
  imgPos,
  videoId,
  componentWidth,
} = toRefs(props);

const observer: Ref<IntersectionObserver | null> = ref(null);
const scrollElement = ref(null);

const onEnter = () => {
  changeTheme(theme.value);
};
const onExit = () => {};

onMounted(() => {
  observer.value = onIntersect(scrollElement.value, onEnter, onExit, false, {
    threshold: 0.6,
  });
});

watch(theme, (value) => {
  if (!value) return;
  changeTheme(value);
});

const themeType = computed(() => {
  return getThemeType(theme.value);
});

const accordionList = computed(() => {
  return JSON.parse(items.value);
});

const showImage = computed(() => {
  return image?.value || videoId?.value ? true : false;
});

const textContainerClasses = computed(() => {
  switch (componentWidth?.value) {
    case "lg":
      return [
        "col-start-2 col-span-12",
        !showImage.value
          ? "lg:flex"
          : imgPos.value === "right"
          ? "lg:col-start-2 lg:col-span-6"
          : "lg:col-start-8 lg:col-span-6",
      ];
    case "sm":
      return `flex flex-col justify-start col-start-2 col-span-12 md:col-start-5 md:col-span-6`;
    default:
      return [
        "col-start-2 col-span-12",
        !showImage.value
          ? "lg:flex"
          : imgPos.value === "right"
          ? "lg:col-start-2 lg:col-span-6"
          : "lg:col-start-8 lg:col-span-6",
      ];
  }
});

const containerStyles = computed(() => {
  const bgColor = isUmbraco.value ? `bg-${theme.value}` : "";
  return `${bgColor} w-full sectionContainer py-20 lg:py-[140px]`;
});

const imageContainerClasses = computed(() => {
  switch (componentWidth?.value) {
    case "lg":
      return [
        "col-start-2 col-span-12 mt-20 lg:mt-0 aspect-portrait",
        imgPos.value === "right"
          ? "lg:col-span-5 lg:col-start-9 lg:row-start-1"
          : "lg:col-span-5 lg:col-start-2 lg:row-start-1",
      ];
    case "sm":
      return `relative flex flex-wrap items-stretch col-span-12 col-start-2 lg:col-span-6 lg:col-start-5 overflow-hidden gap-x-5 lg:gap-x-10`;
    default:
      return [
        "col-start-2 col-span-12 mt-20 lg:mt-0 aspect-portrait",
        imgPos.value === "right"
          ? "lg:col-span-5 lg:col-start-9 lg:row-start-1"
          : "lg:col-span-5 lg:col-start-2 lg:row-start-1",
      ];
  }
});
</script>
